import uniq from 'lodash/uniq'

import { stringToDate } from 'modules/core/lib/dates'
import hasFeature from 'modules/core/lib/hasFeature'
import { apiClient as ApiClient, Collection, Model } from 'modules/core/lib/mobx-rest'
import { employeeUser } from 'modules/core/store/collections/users'

import employeesCollection from 'modules/employees/store/collections/employees'
import { EnabledFeaturesState } from 'modules/shift_management/hooks/reducers/enabledFeatures'
import {
  MAX_ITEMS_PER_PAGE,
  MAX_WORKAREAS_PER_PAGE,
} from 'modules/shift_management/hooks/reducers/pagination'
import buildEmployeeParamsFromResources from 'modules/shift_management/lib/buildEmployeeParamsFromResources'
import buildWorkAreaParamsFromResources, {
  WorkAreaPayload,
} from 'modules/shift_management/lib/buildWorkAreaParamsFromResources'
import {
  ConflictsProps,
  CopyScheduleResponse,
  CopyScheduleStatsResponse,
  EmployeePermissions,
  Filters,
  IExportShiftsSchedule,
  ShiftsRow,
  StatsResponse,
} from 'modules/shift_management/lib/types'
import copyScheduleStats from 'modules/shift_management/store/api/collections/copyScheduleStats'
import defaultShiftsCollection from 'modules/shift_management/store/api/collections/defaultShifts'
import employeesPermissionsCollection from 'modules/shift_management/store/api/collections/employeesPermissions'
import leavesCollection from 'modules/shift_management/store/api/collections/leaves'
import planningVersionsCollection from 'modules/shift_management/store/api/collections/planningVersions'
import restDaysCollection from 'modules/shift_management/store/api/collections/restDays'
import shiftsCollection from 'modules/shift_management/store/api/collections/shifts'
import statsCollection from 'modules/shift_management/store/api/collections/stats'
import templateWeeksCollection from 'modules/shift_management/store/api/collections/templateWeeks'
import unassignedShiftsCollection from 'modules/shift_management/store/api/collections/unassignedShifts'
import workAreasCollection from 'modules/shift_management/store/api/collections/workAreas'
import type EmployeePermission from 'modules/shift_management/store/api/models/EmployeePermission'
import DefaultShifts from 'modules/shift_management/store/internal/collections/DefaultShifts'
import Holidays from 'modules/shift_management/store/internal/collections/Holidays'
import Leaves from 'modules/shift_management/store/internal/collections/Leaves'
import RestDays from 'modules/shift_management/store/internal/collections/RestDays'
import Shifts from 'modules/shift_management/store/internal/collections/Shifts'
import TemplateWeeks from 'modules/shift_management/store/internal/collections/TemplateWeeks'
import UnassignedShifts from 'modules/shift_management/store/internal/collections/UnassignedShifts'
import Leave, { LeavePayload } from 'modules/shift_management/store/internal/models/Leave'
import { PlanningVersionPayload } from 'modules/shift_management/store/internal/models/PlanningVersion'
import RestDay, { RestDayPayload } from 'modules/shift_management/store/internal/models/RestDay'
import { ShiftPayload } from 'modules/shift_management/store/internal/models/Shift'
import type View from 'modules/shift_management/store/internal/models/View'
import type { GroupType, ViewType } from 'modules/shift_management/store/internal/models/View'
// FIXME: Remove this once we migrate to GraphQL
// eslint-disable-next-line factorial/no-allowed-mobx-collections
import companyHolidays from 'modules/timeoff/store/collections/companyHolidays'
import type CompanyHoliday from 'modules/timeoff/store/models/CompanyHoliday'

interface FetchParams {
  startAt: string
  endAt: string
  page?: number | null | undefined
  perPage?: number | null | undefined
  filters: Filters
  groupType: GroupType
  employeeIds?: number[] | null | undefined
  selectableEmployeeIds?: number[] | null | undefined
  features: EnabledFeaturesState
  orderBy?: string | null
  orderDir?: string | null
}

interface FetchStatsParams {
  startAt: string
  endAt: string
  filters: Filters
  view: View
  employeeIds?: number[] | null | undefined
}

interface DateParams {
  startAt: string | null | undefined
  endAt: string | null | undefined
}

interface UnassignedShiftParams extends DateParams {
  features: EnabledFeaturesState
  view: View
}

interface FetchResourcesResponse {
  workAreas?: WorkAreaPayload[]
  employeesPermissions: EmployeePermissions[]
  leaves: LeavePayload[]
  planningVersions: PlanningVersionPayload[]
  shifts: ShiftPayload[]
  holidays: CompanyHoliday[]
  restDays: RestDayPayload[]
}

interface FetchResponse {
  shiftsRows: ShiftsRow[]
  shifts: Shifts | undefined
  leaves: Leaves | undefined
  holidays: Holidays | undefined
  restDays: RestDays | undefined
}

class Api {
  static async fetch(params: FetchParams): Promise<FetchResponse> {
    switch (params.groupType) {
      case 'areas':
        return await Api.fetchWorkAreas(params)

      default:
        return await Api.fetchEmployees(params)
    }
  }

  static async fetchEmployees(params: FetchParams): Promise<FetchResponse> {
    const { employeesPermissions, leaves, planningVersions, shifts, holidays, restDays } =
      await Api.fetchResources({ ...params, perPage: MAX_ITEMS_PER_PAGE })

    return {
      shifts: Shifts.buildFromRequest(shifts),
      leaves: undefined,
      holidays: undefined,
      restDays: undefined,
      shiftsRows: buildEmployeeParamsFromResources(
        employeesPermissions,
        leaves,
        planningVersions,
        shifts,
        holidays,
        restDays
      ),
    }
  }

  static async fetchWorkAreas(params: FetchParams): Promise<FetchResponse> {
    const {
      workAreas,
      employeesPermissions,
      leaves: leavesData,
      planningVersions,
      shifts,
      holidays,
      restDays: restDaysData,
    } = await Api.fetchResources({ ...params, perPage: MAX_WORKAREAS_PER_PAGE })

    const shiftsRows = buildWorkAreaParamsFromResources(
      workAreas,
      employeesPermissions,
      planningVersions,
      shifts,
      holidays
    )

    // FIXME: We’re intentionally using employeesCollection and employeeUser here,
    // even though this is not the correct approach. This is only a temporary solution
    // until the work areas are fully migrated to GraphQL, at which point this code
    // will be removed.
    const leaves = leavesData.map((leave) => {
      const employee = employeesCollection.find({ id: leave.employee_id })
      if (!employee) return new Leave(leave)

      const user = employeeUser(employee)
      return new Leave({
        ...leave,
        employee_full_name: user?.fullName() || '',
        employee_avatar: user?.avatar || '',
      })
    })

    const restDays = restDaysData.map((restDay) => {
      const employee = employeesCollection.find({ id: restDay.employee_id })
      if (!employee) return RestDay.build(restDay)

      const user = employeeUser(employee)
      return RestDay.build({
        ...restDay,
        employeeFullName: user?.fullName() || '',
        employeeAvatar: user?.avatar || '',
      })
    })

    return {
      leaves: new Leaves(leaves),
      holidays: Holidays.buildFromProps(holidays),
      restDays: new RestDays(restDays),
      shifts: Shifts.buildFromRequest(shifts),
      shiftsRows,
    }
  }

  static async fetchStats(params: FetchStatsParams): Promise<StatsResponse> {
    const { startAt, endAt, employeeIds, filters } = params

    const [statsData] = await Promise.all([
      statsCollection.rpc('', {
        start_date: startAt,
        end_date: endAt,
        employee_ids: employeeIds,
        ...filters,
      }),
    ])

    return { stats: statsData }
  }

  static async fetchDay(
    dayId: string,
    filters: Filters,
    features: EnabledFeaturesState
  ): Promise<FetchResponse> {
    const { employeesPermissions, leaves, planningVersions, shifts, holidays, restDays } =
      await Api.fetchResources({
        startAt: dayId,
        endAt: dayId,
        filters,
        groupType: 'employees',
        features,
      })

    return {
      shifts: undefined,
      leaves: undefined,
      holidays: undefined,
      restDays: undefined,
      shiftsRows: buildEmployeeParamsFromResources(
        employeesPermissions,
        leaves,
        planningVersions,
        shifts,
        holidays,
        restDays
      ),
    }
  }

  static async publish(
    startAt: string,
    endAt: string,
    filters: Filters,
    sendNotifications: boolean,
    viewType: ViewType
  ): Promise<void> {
    const api = ApiClient()

    return await api.post('/shift_management/schedules/publish', {
      start_at: startAt,
      end_at: endAt,
      ...filters,
      send_notifications: sendNotifications,
      view_type: viewType,
    })
  }

  static async exportShiftsSchedule(payload: IExportShiftsSchedule): Promise<void> {
    const api = ApiClient()
    const url = '/shift_management/schedules/exports'

    return await api.post(url, payload)
  }

  static async fetchCopyScheduleStats(
    originalStartAt: string,
    filters: Filters
  ): Promise<CopyScheduleStatsResponse> {
    const params = {
      original_start_at: originalStartAt,
      ...filters,
    }

    // FIXME: We do RPC because there are companies with too employee_ids (~900employees) and
    // with GET, it throws CORS error, but we should migrate this to backend filters.
    return await copyScheduleStats.rpc('', params)
  }

  static async copySchedule(
    originalStartAt: string,
    targetStartAt: string,
    filters: Filters
  ): Promise<CopyScheduleResponse> {
    const api = ApiClient()

    // Send days_to_copy to 7, 28 or the month's days to copy based on view (week, 4weeks, month)
    const params = {
      original_start_at: originalStartAt,
      target_start_at: targetStartAt,
      ...filters,
    }

    const url = '/shift_management/schedules/bulk_copy'

    return await api.post(url, params)
  }

  static async checkOverlappingShifts(
    selectedTemplateId: number,
    weekPosition: number,
    employeeIds: number[],
    startDateParam: string,
    endDateParam: string
  ): Promise<ConflictsProps[]> {
    const api = ApiClient()

    const params = {
      template_id: selectedTemplateId,
      template_week_position: weekPosition,
      employee_ids: employeeIds,
      start_date: startDateParam,
      end_date: endDateParam,
    }

    const url = '/shift_management/overlaps_shift_templates'

    return await api.get(url, params)
  }

  static maybeFetchCollection<T extends Model>(
    collection: Collection<T>,
    params: Filters | Record<string, unknown>,
    skip = false
  ) {
    return skip ? collection.toJS() : collection.rpcWithSet('', params)
  }

  static async fetchResources(params: FetchParams): Promise<FetchResourcesResponse> {
    const {
      startAt,
      endAt,
      page,
      perPage,
      filters,
      employeeIds,
      selectableEmployeeIds,
      groupType,
      orderBy,
      orderDir,
    } = params
    let filterParams: Filters = {
      start_at: startAt,
      end_at: endAt,
      location_ids: filters.locationIds,
      ...filters,
      // Keep this filter with OR instead of {employee_ids: employeeIds, ...filters} to avoid overwriting
      // since no matter what the employeeIds has, the filters always have employee_ids
      employee_ids: employeeIds || filters.employee_ids,
      order_by: orderBy,
      order_dir: orderDir,
    }

    const isWorkAreaNthPage = groupType === 'areas' && page != 1

    let workAreas = []
    let employeesPermissions = []
    if (hasFeature('dev_location_work_areas') && groupType === 'areas') {
      ;[workAreas, employeesPermissions] = await Promise.all([
        workAreasCollection.rpc('', { page, per_page: perPage, ...filterParams }),
        this.maybeFetchCollection(
          employeesPermissionsCollection,
          // Avoid this location_ids filter because we are filtering the work areas, not the employees.
          { ...filterParams, location_ids: undefined },
          isWorkAreaNthPage
        ),
      ])
    } else {
      filterParams = { page, per_page: perPage, ...filterParams }
      employeesPermissions = await employeesPermissionsCollection.rpcWithSet('', filterParams)
    }

    const locationIds =
      groupType === 'areas' ? uniq(workAreas.map((wa: WorkAreaPayload) => wa.location_id)) : null

    const employeesIds = employeesPermissions.map((e: EmployeePermission) => e.id)
    const leavesEmployeeIds = selectableEmployeeIds || employeesIds
    const resourcesParams = { start_at: startAt, end_at: endAt, employee_ids: employeesIds }
    const leavesResourcesParams = {
      start_at: startAt,
      end_at: endAt,
      employee_ids: leavesEmployeeIds,
    }
    const skipRestRays = !hasFeature('shift_management_rest_day') || isWorkAreaNthPage
    const [leaves, planningVersions, shifts, restDays] = await Promise.all([
      this.maybeFetchCollection(leavesCollection, leavesResourcesParams, isWorkAreaNthPage),
      this.maybeFetchCollection(planningVersionsCollection, resourcesParams, isWorkAreaNthPage),
      shiftsCollection.rpcWithSet('', {
        ...resourcesParams,
        shift_ids: filters.shift_ids,
        without_ids: filters.without_shifts_ids,
        location_ids: locationIds,
      }),
      this.maybeFetchCollection(restDaysCollection, resourcesParams, skipRestRays),
    ])

    // CompanyHolidays are fetched at the beginning, so we don't need to do
    // an extra request for getting them
    const employees = employeesCollection.filter((e) => employeesIds.includes(e.id))
    const holidays = companyHolidays.filterByRangeAndLocation(
      employees.map((e) => Number(e.attributes.location_id)),
      stringToDate(startAt, 'backend'),
      stringToDate(endAt, 'backend')
    )

    return { workAreas, employeesPermissions, leaves, planningVersions, shifts, holidays, restDays }
  }

  static async fetchEmployeesPermissions(params: DateParams): Promise<EmployeePermissions[]> {
    const filterParams: Filters = {
      start_at: params.startAt,
      end_at: params.endAt,
    }

    return await employeesPermissionsCollection.rpc('', filterParams)
  }

  static async fetchDefaultShifts(): Promise<DefaultShifts> {
    const defaultShiftsPayload = await defaultShiftsCollection.fetch()
    const defaultShifts = DefaultShifts.buildFromProps(defaultShiftsPayload)

    return defaultShifts
  }

  static async fetchTemplateWeeks(): Promise<TemplateWeeks> {
    const templateWeeksPayload = await templateWeeksCollection.fetch()
    const templateWeeks = TemplateWeeks.buildFromProps(templateWeeksPayload)

    return templateWeeks
  }

  static async fetchUnassignedShifts({
    features,
    startAt,
    endAt,
    view,
  }: UnassignedShiftParams): Promise<UnassignedShifts> {
    const hasCoverageView = features.canUseCoverageView
    const fetchUnassignedShifts = hasCoverageView && view.isByEmployees && view.isWeekly

    const unassignedShiftsPayload = fetchUnassignedShifts
      ? await unassignedShiftsCollection.fetch({
          data: { start_at: startAt, end_at: endAt },
        })
      : []

    const unassignedShifts = UnassignedShifts.buildFromProps(unassignedShiftsPayload)

    return unassignedShifts
  }
}

export default Api
